// Per-route <head> for the public pages. One source for both:
//  - the pre-rendered HTML written at build time (vite.config.ts -> src/entry-prerender.tsx), and
//  - client-side navigation (main.tsx calls applyHead on every route change).
// App screens (Earn, Wallet, ...) get "<Screen> · GaintPlay", the default description and no
// canonical; unknown paths also get noindex. If the site moves to another host, change SITE_URL.
//
// Everything is in the reader's language (t() at the time the head is written). Each public page
// has one address per language: the English one as it is, the others with ?lang=xx (the web Worker
// serves that language's pre-rendered page). Every version names itself canonical and lists the
// others (hreflang), with the English as x-default; the sitemap does the same.
import { screenFor } from '../components/Layout';
import { faqCount, faqPageSchema } from '../pages/info/faq-markup';
import { AVAILABLE, LOCALE_INFO, getLocale, msg, t, type Locale } from '../i18n';
import { currentFaq } from './faq';

export const SITE_URL = 'https://gaintplay.com';
const SITE_NAME = 'GaintPlay';
const OG_IMAGE = {
  url: `${SITE_URL}/brand/og-1200x630.png`,
  width: '1200',
  height: '630',
  // i18n: alt text of the picture shown when a GaintPlay link is shared
  alt: msg('GaintPlay: get paid to play games, take surveys and try apps. 1,000 coins = $1.'),
};

// i18n: the site's default page title (browser tab, search results): under 60 characters
export const DEFAULT_TITLE = msg('GaintPlay · Get paid to play games, take surveys, try apps');
// i18n: the site's default description in search results: under 155 characters
export const DEFAULT_DESCRIPTION = msg(
  'Earn coins for games, surveys and app offers from trusted partners. 1,000 coins = $1. Cash out to gift cards from $1. Free to join, no card needed.',
);

type JsonLd = Record<string, unknown>;

export interface PageMeta {
  path: string;
  /** Short name used in breadcrumbs (English, marked: shown with t()). */
  name: string;
  /** Under 60 characters (English, marked: shown with t()). */
  title: string;
  /** Under 155 characters (English, marked: shown with t()). */
  description: string;
  /** Structured data besides the BreadcrumbList every subpage gets. */
  schema?: () => JsonLd[];
}

const ORG_ID = `${SITE_URL}/#organization`;

/** The public, indexable pages: pre-rendered at build and listed in sitemap.xml, in this order. */
export const PUBLIC_PAGES: readonly PageMeta[] = [
  {
    path: '/',
    name: msg('Home'),
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    schema: () => [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': ORG_ID,
        name: SITE_NAME,
        url: `${SITE_URL}/`,
        logo: `${SITE_URL}/brand/app-512.png`,
        email: 'support@gaintplay.com',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'support@gaintplay.com',
          availableLanguage: AVAILABLE.map((l) => LOCALE_INFO[l].english),
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: SITE_NAME,
        url: `${SITE_URL}/`,
        inLanguage: getLocale(),
        publisher: { '@id': ORG_ID },
      },
    ],
  },
  {
    path: '/how-it-works',
    name: msg('How it works'),
    title: msg('How GaintPlay works: from first task to gift card'),
    description: msg(
      'Sign up free, finish a partner task and get coins once it is confirmed: 1,000 coins = $1. Most credits land in minutes; bigger ones wait 7 to 30 days.',
    ),
  },
  {
    path: '/ways-to-earn',
    name: msg('Ways to earn'),
    title: msg('Ways to earn on GaintPlay: surveys, games and apps'),
    description: msg(
      'Earn coins from surveys, game milestones, app installs and offers, plus a 7-day daily bonus streak and 5% of what friends you invite earn from offers.',
    ),
  },
  {
    path: '/gift-cards',
    name: msg('Cash out'),
    title: msg('Cash out to gift cards from $1 · GaintPlay'),
    description: msg(
      'Swap coins for gift cards from $1: 1,000 coins = $1 and coins never expire. After a quick review the code shows in your account and arrives by email.',
    ),
  },
  {
    path: '/faq',
    name: msg('FAQ'),
    title: msg('GaintPlay FAQ: pending coins, reversals and cash-outs'),
    description: msg(
      'Straight answers on pending and missing coins, reversals, VPNs, one account per person, countries, cash-out minimums, delivery and deleting your account.',
    ),
    // The questions the page shows: built in at build time; the live FAQ in the browser and in
    // what the web Worker serves (it rewrites this block, see pages/info/faq-markup.ts). None
    // while the page shows no questions.
    schema: () => {
      const groups = currentFaq();
      return groups && faqCount(groups) ? [faqPageSchema(groups)] : [];
    },
  },
  {
    path: '/leaderboard',
    name: msg('Leaderboard'),
    title: msg('Leaderboard: top earners today · GaintPlay'),
    description: msg(
      'See who earned the most coins from offers on GaintPlay today, in the last 7 days and this month. Reversed offers don’t count.',
    ),
  },
  {
    path: '/terms',
    name: msg('Terms of Service'),
    title: msg('Terms of Service · GaintPlay'),
    description: msg(
      'The rules for using GaintPlay: one account per person, how coins and holds work, and how every cash-out is reviewed before it is sent.',
    ),
  },
  {
    path: '/privacy',
    name: msg('Privacy Policy'),
    title: msg('Privacy Policy · GaintPlay'),
    description: msg(
      'What GaintPlay collects (email, tasks, device and IP data), why, who it is shared with, and how to ask for a copy of your data or have it deleted.',
    ),
  },
];

const BY_PATH = new Map(PUBLIC_PAGES.map((p) => [p.path, p]));

/** "/faq/" and "/faq" are the same page. */
const normalise = (pathname: string) => pathname.replace(/\/+$/, '') || '/';

export function metaFor(pathname: string): PageMeta | undefined {
  return BY_PATH.get(normalise(pathname));
}

/** A page's address in a language: English as it is, the others with ?lang=xx. */
export const canonicalUrl = (path: string, locale: Locale = getLocale()) =>
  (path === '/' ? `${SITE_URL}/` : SITE_URL + path) + (locale === 'en' ? '' : `?lang=${locale}`);

/** The page in every language, for search engines (hreflang), English also as x-default. */
const alternates = (path: string) => [
  ...AVAILABLE.map((l) => ({ hreflang: l as string, href: canonicalUrl(path, l) })),
  { hreflang: 'x-default', href: canonicalUrl(path, 'en') },
];

/** og:locale wants the region too, with an underscore (de_DE, pt_BR). */
const ogLocale = (l: Locale) => LOCALE_INFO[l].intl.replace('-', '_');

function jsonLdFor(meta: PageMeta): JsonLd[] {
  const out = meta.schema?.() ?? [];
  if (meta.path !== '/')
    out.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: t('Home'), item: canonicalUrl('/') },
        { '@type': 'ListItem', position: 2, name: t(meta.name), item: canonicalUrl(meta.path) },
      ],
    });
  return out;
}

/** JSON for a <script> body: "<" escaped so text can never close the tag. */
const ldJson = (data: JsonLd) => JSON.stringify(data).replace(/</g, '\\u003c');

const attr = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * The <head> tags for a pre-rendered page (replaces the <!--seo--> block of index.html), in the
 * language it is rendered in.
 */
export function headHtml(meta: PageMeta): string {
  const locale = getLocale();
  const url = canonicalUrl(meta.path);
  const title = t(meta.title);
  const description = t(meta.description);
  const tags = [
    `<title>${attr(title)}</title>`,
    `<meta name="description" content="${attr(description)}" />`,
    `<link rel="canonical" href="${url}" />`,
    ...alternates(meta.path).map(
      (a) => `<link rel="alternate" hreflang="${a.hreflang}" href="${a.href}" />`,
    ),
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="${SITE_NAME}" />`,
    `<meta property="og:locale" content="${ogLocale(locale)}" />`,
    ...AVAILABLE.filter((l) => l !== locale).map(
      (l) => `<meta property="og:locale:alternate" content="${ogLocale(l)}" />`,
    ),
    `<meta property="og:title" content="${attr(title)}" />`,
    `<meta property="og:description" content="${attr(description)}" />`,
    `<meta property="og:url" content="${url}" />`,
    `<meta property="og:image" content="${OG_IMAGE.url}" />`,
    `<meta property="og:image:width" content="${OG_IMAGE.width}" />`,
    `<meta property="og:image:height" content="${OG_IMAGE.height}" />`,
    `<meta property="og:image:alt" content="${attr(t(OG_IMAGE.alt))}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${attr(title)}" />`,
    `<meta name="twitter:description" content="${attr(description)}" />`,
    ...jsonLdFor(meta).map((d) => `<script type="application/ld+json">${ldJson(d)}</script>`),
  ];
  return tags.join('\n    ');
}

/** sitemap.xml for the public pages: every language's address, each listing the others. */
export function sitemapXml(lastmod: string): string {
  const urls = PUBLIC_PAGES.flatMap((p) =>
    AVAILABLE.map((l) =>
      [
        `  <url>`,
        `    <loc>${attr(canonicalUrl(p.path, l))}</loc>`,
        ...alternates(p.path).map(
          (a) =>
            `    <xhtml:link rel="alternate" hreflang="${a.hreflang}" href="${attr(a.href)}" />`,
        ),
        `    <lastmod>${lastmod}</lastmod>`,
        `  </url>`,
      ].join('\n'),
    ),
  );
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">\n${urls.join('\n')}\n</urlset>\n`;
}

/* ------------------------------------------------------------------ Client-side updates */

function setMeta(key: 'name' | 'property', id: string, content: string | null) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${id}"]`);
  if (content === null) return el?.remove();
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, id);
    document.head.appendChild(el);
  }
  if (el.content !== content) el.content = content;
}

/** The hreflang links: this page in every language, or none (app screens). */
function setAlternates(path: string | null) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  if (path === null) return;
  for (const a of alternates(path)) {
    const el = document.createElement('link');
    el.rel = 'alternate';
    el.hreflang = a.hreflang;
    el.href = a.href;
    document.head.appendChild(el);
  }
}

function setCanonical(href: string | null) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === null) return el?.remove();
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  if (el.href !== href) el.href = href;
}

function setJsonLd(items: JsonLd[]) {
  const next = items.map(ldJson);
  const current = [...document.head.querySelectorAll('script[type="application/ld+json"]')];
  if (current.length === next.length && current.every((s, i) => s.textContent === next[i])) return;
  current.forEach((s) => s.remove());
  for (const json of next) {
    // A data block, never executed, so the CSP's script-src doesn't apply to it.
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.textContent = json;
    document.head.appendChild(s);
  }
}

const AUTH_TITLES: Record<string, string> = {
  '/signup': msg('Sign up'),
  '/login': msg('Log in'),
  '/forgot-password': msg('Reset your password'),
};

/**
 * Bring <head> in line with the current route after a client-side navigation. Idempotent, so it
 * is safe to call on every router update (the pre-rendered head already matches on first load).
 */
export function applyHead(pathname: string, { notFound = false } = {}) {
  const meta = notFound ? undefined : metaFor(pathname);
  setMeta('property', 'og:locale', ogLocale(getLocale()));
  if (meta) {
    const url = canonicalUrl(meta.path);
    const title = t(meta.title);
    const description = t(meta.description);
    document.title = title;
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'robots', null);
    setCanonical(url);
    setAlternates(meta.path);
    setJsonLd(jsonLdFor(meta));
    return;
  }
  const path = normalise(pathname);
  const screen = notFound
    ? t('Page not found')
    : t(AUTH_TITLES[path] ?? screenFor(path).title ?? '');
  // i18n: a page title in the browser tab, e.g. "Wallet · GaintPlay"
  const title = screen ? t('{screen} · GaintPlay', { screen }) : t(DEFAULT_TITLE);
  const description = t(DEFAULT_DESCRIPTION);
  document.title = title;
  setMeta('name', 'description', description);
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', null);
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'robots', notFound ? 'noindex' : null);
  setCanonical(null);
  setAlternates(null);
  setJsonLd([]);
}
